import { useEffect, useState } from "react";
import { toast } from "sonner";
import { SlidersHorizontal, ChevronDown, Loader2 } from "lucide-react";
import { api, fmtErr } from "@/lib/api";
import JobScoringEditor from "./JobScoringEditor";
import { scoringSummary, fullScoring } from "./scoringModel";

// Job page card: edit weights and recalculate every candidate's scores in this job.
export default function JobScoringCard({ job, canEdit, onSaved }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(job.scoring || null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setDraft(job.scoring || null); }, [job.scoring]);
  const dirty = JSON.stringify(fullScoring(draft, job.stages)) !== JSON.stringify(fullScoring(job.scoring, job.stages)) || (!job.scoring && !!draft);

  const reset = async () => {
    if (!window.confirm("Reset to the default equal weighting and the AI's original round scores? Every candidate in this job is recalculated.")) return;
    setSaving(true);
    try {
      const { data } = await api.delete(`/jobs/${job.id}/scoring`);
      toast.success(`Scoring reset · ${data.recalculated} candidate${data.recalculated === 1 ? "" : "s"} recalculated`);
      onSaved?.();
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
    setSaving(false);
  };
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/jobs/${job.id}/scoring`, fullScoring(draft, job.stages));
      toast.success(`Scoring saved · ${data.recalculated} candidate${data.recalculated === 1 ? "" : "s"} recalculated`);
      onSaved?.();
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
    setSaving(false);
  };

  return (
    <div className="tp-card mb-6" data-testid="job-scoring-card">
      <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open} data-testid="job-scoring-toggle"
        className="w-full flex items-center gap-3 px-5 py-4 text-left">
        <span className="w-9 h-9 rounded-lg bg-amber-50 text-amber-600 grid place-items-center shrink-0"><SlidersHorizontal className="w-4.5 h-4.5" /></span>
        <span className="min-w-0">
          <span className="block font-display font-semibold">Job Scoring</span>
          <span className="block text-xs text-slate-500 truncate" data-testid="job-scoring-summary">{scoringSummary(job.scoring, job.stages)}{job.scoring ? "" : " (default)"}</span>
        </span>
        <ChevronDown className={`w-4 h-4 ml-auto text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-5 pb-5 border-t border-[color:var(--bd-sub)] pt-4">
          <p className="text-sm text-slate-500 mb-4 max-w-3xl">Decide what matters most for this role. Round weights set how the overall score is built; skill weights set how each round's score is built from the AI's sub-scores. Saving recalculates every candidate in this job. The AI's written verdict isn't changed.</p>
          {canEdit ? (
            <>
              <JobScoringEditor value={draft} onChange={setDraft} stages={job.stages} prefix="jobpage" />
              <div className="flex flex-wrap justify-end gap-2 mt-5">
                {job.scoring && <button type="button" data-testid="job-scoring-reset" disabled={saving} onClick={reset} className="mr-auto px-3 py-2 text-sm text-slate-500 hover:text-rose-600 transition-colors">Reset to default</button>}
                {dirty && <button type="button" data-testid="job-scoring-cancel" onClick={() => setDraft(job.scoring || null)} className="px-4 py-2 text-sm text-slate-600 hover:text-slate-900">Discard</button>}
                <button type="button" data-testid="job-scoring-save" disabled={!dirty || saving} onClick={save}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white disabled:opacity-40 transition-colors">
                  {saving && <Loader2 className="w-4 h-4 animate-spin" />} Save & recalculate
                </button>
              </div>
            </>
          ) : <p className="text-sm text-slate-500" data-testid="job-scoring-readonly">Only the job owner or an admin can change the scoring.</p>}
        </div>
      )}
    </div>
  );
}
